/**
 * The Request Board's board-level surfaces:
 *   - HistoryPanel — the History tab: every update on the board, newest first
 *     (AG Grid), with a kind filter, a search box, and who each change alerted.
 *   - AlertsDialog — the viewer's alert rules: which cards to follow, an Email
 *     and an In-app switch per situation, and how emails are delivered.
 */
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CloseRounded from "@mui/icons-material/CloseRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import SearchInput from "../../components/inputs/SearchInput";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { slate, status, steel } from "../../theme/colors";
import { segmentedGroupSx } from "../../theme/activeStates";
import { PillSwitcher } from "../admin/shared/PillSwitcher";
import { A } from "../admin/shared/adminTokens";
import { COLUMNS, personFor } from "./requestBoardModel";
import {
  ALERT_GROUPS,
  ALERT_RULES,
  DELIVERY_MODES,
  EVENT,
  EVENT_LABEL,
  HISTORY_FILTERS,
  describeEvent,
  rulesOn,
} from "./requestBoardActivity";
import { Avatar, Pill } from "./requestBoardParts";

/* ─────────────────────────────── history ─────────────────────────────── */

const KIND_TONE = {
  [EVENT.CREATED]: { bg: steel.tint, fg: steel.main },
  [EVENT.MOVED]: { bg: status.warning.bg, fg: status.warning.fg },
  [EVENT.REORDERED]: { bg: slate[100], fg: slate[500] },
  [EVENT.COMMENTED]: { bg: status.success.bg, fg: status.success.fg },
  [EVENT.IMAGES]: { bg: slate[100], fg: slate[500] },
  [EVENT.EDITED]: { bg: slate[100], fg: slate[700] },
  [EVENT.DELETED]: { bg: status.error.bg, fg: status.error.fg },
};

const Center = ({ children, jc }) => (
  <Flx ac jc={jc} g={0.75} sx={{ height: "100%" }}>
    {children}
  </Flx>
);

Center.propTypes = { children: PropTypes.node, jc: PropTypes.bool };

const names = (keys) => (keys || []).map((p) => personFor(p)?.name || p).join(", ");

/**
 * The board's History tab: every update, newest first (AG Grid), with a kind
 * filter, a search box, and who each change alerted.
 */
export const HistoryPanel = ({ events, onOpenItem, itemExists }) => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const kinds = HISTORY_FILTERS.find((f) => f.value === filter)?.kinds;
    return (kinds ? events.filter((e) => kinds.includes(e.kind)) : events).map((e) => ({
      ...e,
      who: personFor(e.actor)?.name || e.actor,
      details: describeEvent(e),
      emailedNames: names(e.emailed),
      notifiedNames: names(e.notified),
    }));
  }, [events, filter]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "When",
        field: "at",
        sort: "desc",
        width: 170,
        valueFormatter: (p) => formatDateTime(p.value, "datetime"),
      },
      {
        headerName: "Who",
        field: "who",
        width: 150,
        cellRenderer: (p) => (
          <Center>
            <Avatar person={p.data.actor} size={20} />
            <span>{p.value}</span>
          </Center>
        ),
      },
      {
        headerName: "Change",
        field: "kind",
        width: 140,
        valueGetter: (p) => EVENT_LABEL[p.data.kind],
        cellRenderer: (p) => (
          <Center>
            <Pill {...KIND_TONE[p.data.kind]}>{p.value}</Pill>
          </Center>
        ),
      },
      {
        headerName: "Item",
        field: "itemTitle",
        flex: 1.2,
        minWidth: 240,
        tooltipField: "itemTitle",
        cellRenderer: (p) =>
          itemExists(p.data.itemId) ? (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenItem(p.data.itemId);
              }}
              sx={{
                p: 0,
                border: "none",
                background: "none",
                font: "inherit",
                color: A.tealDark,
                cursor: "pointer",
                textAlign: "left",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {p.value}
            </Box>
          ) : (
            <Box component="span" sx={{ color: A.muted }}>
              {p.value}
            </Box>
          ),
      },
      { headerName: "Details", field: "details", flex: 1.5, minWidth: 240, tooltipField: "details" },
      {
        headerName: "In app",
        field: "notifiedNames",
        width: 140,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Emailed",
        field: "emailedNames",
        width: 140,
        valueFormatter: (p) => p.value || "—",
      },
    ],
    [itemExists, onOpenItem],
  );

  return (
    <Flx column sx={{ flex: 1, minHeight: 0 }}>
      <Flx ac jb g={1.5} wrap sx={{ mb: 1.5, flexShrink: 0 }}>
        <PillSwitcher
          items={HISTORY_FILTERS.map((f) => ({ key: f.value, label: f.label }))}
          value={filter}
          onChange={setFilter}
        />
        <Box sx={{ width: 280 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search history" />
        </Box>
      </Flx>
      <Box sx={{ flex: 1, minHeight: 320 }}>
        <AgGridTableWrapper fullHeight sx={{ height: "100%" }}>
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, sortable: true, suppressHeaderMenuButton: true }}
            getRowId={(p) => p.data.id}
            quickFilterText={search}
            tooltipShowDelay={400}
            pagination
            paginationPageSize={50}
          />
        </AgGridTableWrapper>
      </Box>
    </Flx>
  );
};

HistoryPanel.propTypes = {
  events: PropTypes.array.isRequired,
  onOpenItem: PropTypes.func.isRequired,
  itemExists: PropTypes.func.isRequired,
};

/* ─────────────────────────────── alerts ──────────────────────────────── */

const SCOPE_OPTIONS = [
  { value: "mine", label: "Only mine" },
  { value: "all", label: "All cards" },
];

/** Segmented control (level 3 — it changes a setting, not the page). */
const Segmented = ({ value, options, onChange, label }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    aria-label={label}
    value={value}
    onChange={(_, v) => v && onChange(v)}
    sx={{ ...segmentedGroupSx({ fontSize: 13 }), border: `1px solid ${A.line}`, flexShrink: 0 }}
  >
    {options.map((o) => (
      <ToggleButton key={o.value} value={o.value}>
        {o.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

Segmented.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

/** A white rounded card on the dialog's gray body. */
const Card = ({ children }) => (
  <Box
    sx={{
      border: `1px solid ${A.line}`,
      borderRadius: "12px",
      bgcolor: A.surface,
      overflow: "hidden",
      "& > * + *": { borderTop: `1px solid ${A.line}` },
    }}
  >
    {children}
  </Box>
);

Card.propTypes = { children: PropTypes.node };

const RowText = ({ title, sub, muted }) => (
  <Flx column g={0.25} sx={{ minWidth: 0, flex: 1 }}>
    <Txt sx={{ fontSize: 14, fontWeight: 500, color: muted ? A.muted : A.ink }}>{title}</Txt>
    {sub ? <Txt sx={{ fontSize: 12.5, color: A.muted }}>{sub}</Txt> : null}
  </Flx>
);

RowText.propTypes = { title: PropTypes.node, sub: PropTypes.node, muted: PropTypes.bool };

/** Width of the Email / In app switch columns, so headers and switches align. */
const CH_W = 64;

/** The stages a "enters / leaves a stage" rule watches. */
const StagePicker = ({ value, onChange, disabled }) => (
  <Select
    multiple
    size="small"
    displayEmpty
    disabled={disabled}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    renderValue={(sel) =>
      sel.length ? (
        <Flx ac g={0.5} wrap>
          {sel.map((k) => {
            const c = COLUMNS.find((x) => x.key === k);
            return (
              <Flx
                key={k}
                ac
                g={0.75}
                sx={{ px: 1, py: "1px", borderRadius: "6px", bgcolor: slate[100], fontSize: 13 }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c?.dot }} />
                {c?.label}
              </Flx>
            );
          })}
        </Flx>
      ) : (
        <Box component="span" sx={{ color: A.faint, fontSize: 13.5 }}>
          Choose stages
        </Box>
      )
    }
    sx={{ mt: 1.25, minWidth: 180, fontSize: 13.5, "& .MuiSelect-select": { py: "7px" } }}
  >
    {COLUMNS.map((c) => (
      <MenuItem key={c.key} value={c.key} sx={{ fontSize: 13.5, gap: 1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: c.dot }} />
        {c.label}
      </MenuItem>
    ))}
  </Select>
);

StagePicker.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const RuleRow = ({ rule, value, onChange }) => {
  const off = !value.email && !value.app;
  return (
    <Flx g={2} sx={{ px: 2.5, py: 1.75, alignItems: "flex-start" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <RowText title={rule.title} sub={rule.sub} muted={off} />
        {rule.stages ? (
          <StagePicker
            value={value.stages || []}
            disabled={off}
            onChange={(stages) => onChange({ ...value, stages })}
          />
        ) : null}
      </Box>
      {["email", "app"].map((ch) => (
        <Flx key={ch} jc sx={{ width: CH_W, flexShrink: 0 }}>
          <Switch
            checked={!!value[ch]}
            onChange={(e) => onChange({ ...value, [ch]: e.target.checked })}
            inputProps={{ "aria-label": `${rule.title}: ${ch === "email" ? "email" : "in app"}` }}
          />
        </Flx>
      ))}
    </Flx>
  );
};

RuleRow.propTypes = {
  rule: PropTypes.object.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const GroupHead = ({ label }) => (
  <Flx ac g={2} sx={{ px: 2.5, py: 1.25, bgcolor: A.surface }}>
    <Txt
      sx={{
        flex: 1,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: A.ink2,
      }}
    >
      {label}
    </Txt>
    {["Email", "In app"].map((h) => (
      <Txt
        key={h}
        sx={{
          width: CH_W,
          textAlign: "center",
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          color: A.muted,
        }}
      >
        {h}
      </Txt>
    ))}
  </Flx>
);

GroupHead.propTypes = { label: PropTypes.string };

/** "Email delivery" row: text on the left, control on the right. */
const SettingRow = ({ title, sub, control, children }) => (
  <Box sx={{ px: 2.5, py: 1.75 }}>
    <Flx ac g={2}>
      <RowText title={title} sub={sub} />
      {control}
    </Flx>
    {children}
  </Box>
);

SettingRow.propTypes = {
  title: PropTypes.node,
  sub: PropTypes.node,
  control: PropTypes.node,
  children: PropTypes.node,
};

export const AlertsDialog = ({ open, prefs, onClose, onSave, onTestEmail }) => {
  const [testing, setTesting] = useState(false);
  const email = useSelector((state) => state.user?.emailAddress);
  const [tab, setTab] = useState("what");
  const [draft, setDraft] = useState(prefs);
  useEffect(() => {
    if (open) {
      setDraft(prefs);
      setTab("what");
    }
  }, [open, prefs]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(prefs);
  const setRule = (key, value) =>
    setDraft((prev) => ({ ...prev, rules: { ...prev.rules, [key]: value } }));
  const setDelivery = (patch) =>
    setDraft((prev) => ({ ...prev, delivery: { ...prev.delivery, ...patch } }));
  const { delivery } = draft;
  const modeLabel = DELIVERY_MODES.find((m) => m.key === delivery.mode)?.label;

  const tabLabel = (title, meta) => (
    <Flx ac g={0.75}>
      <span>{title}</span>
      <Box component="span" sx={{ fontWeight: 400, color: A.faint, fontSize: 13 }}>
        {meta}
      </Box>
    </Flx>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 720,
          maxWidth: "calc(100% - 32px)",
          height: "min(820px, calc(100% - 64px))",
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      <Flx column sx={{ height: "100%" }}>
        <Box sx={{ px: 3, pt: 2.5, flexShrink: 0, borderBottom: `1px solid ${A.line}` }}>
          <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
            <Flx column g={0.25}>
              <Txt sx={{ fontSize: 20, fontWeight: 700, color: A.ink }}>Alerts</Txt>
              <Txt sx={{ fontSize: 13.5, color: A.muted }}>
                Choose what you hear about, and how.
              </Txt>
            </Flx>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Flx>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mt: 1.5, minHeight: 0, "& .MuiTab-root": { minHeight: 44, px: 0, mr: 3.5 } }}
          >
            <Tab
              value="what"
              label={tabLabel("What to alert me about", `${rulesOn(draft)} of ${ALERT_RULES.length} on`)}
            />
            <Tab value="delivery" label={tabLabel("Email delivery", modeLabel)} />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", bgcolor: slate[50], p: 3 }}>
          {tab === "what" ? (
            <Flx column g={2}>
              <Card>
                <SettingRow
                  title="Cards to follow"
                  sub="Applies to stage changes and comments"
                  control={
                    <Segmented
                      label="Cards to follow"
                      value={draft.scope}
                      options={SCOPE_OPTIONS}
                      onChange={(scope) => setDraft((prev) => ({ ...prev, scope }))}
                    />
                  }
                />
              </Card>
              {ALERT_GROUPS.map((g) => (
                <Card key={g.key}>
                  <GroupHead label={g.label} />
                  {ALERT_RULES.filter((r) => r.group === g.key).map((r) => (
                    <RuleRow
                      key={r.key}
                      rule={r}
                      value={draft.rules[r.key]}
                      onChange={(v) => setRule(r.key, v)}
                    />
                  ))}
                </Card>
              ))}
            </Flx>
          ) : (
            <Card>
              <SettingRow
                title="Send emails"
                sub="Group alerts so your inbox stays quiet"
                control={
                  <Segmented
                    label="Send emails"
                    value={delivery.mode}
                    options={DELIVERY_MODES.map((m) => ({ value: m.key, label: m.label }))}
                    onChange={(mode) => setDelivery({ mode })}
                  />
                }
              />
              <SettingRow
                title="Pause on weekends"
                sub="No emails on Saturday or Sunday"
                control={
                  <Switch
                    checked={delivery.weekends}
                    onChange={(e) => setDelivery({ weekends: e.target.checked })}
                    inputProps={{ "aria-label": "Pause on weekends" }}
                  />
                }
              />
              <SettingRow
                title="Send to"
                sub="The email address you sign in with"
                control={
                  <Flx
                    ac
                    g={0.75}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "8px",
                      border: `1px solid ${A.line}`,
                      bgcolor: slate[50],
                      fontSize: 13.5,
                      color: A.ink,
                      minWidth: 0,
                    }}
                  >
                    <MailOutlineRounded sx={{ fontSize: 17, color: A.muted }} />
                    <Box
                      component="span"
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {email || "Your sign-in email"}
                    </Box>
                  </Flx>
                }
              />
            </Card>
          )}
        </Box>

        <Flx
          ac
          jb
          g={1.5}
          sx={{ px: 3, py: 2, borderTop: `1px solid ${A.line}`, flexShrink: 0 }}
        >
          <Button
            variant="text"
            color="gray"
            startIcon={<MailOutlineRounded />}
            disabled={testing}
            onClick={async () => {
              setTesting(true);
              await onTestEmail();
              setTesting(false);
            }}
          >
            Send a test email
          </Button>
          <Flx ac g={1}>
            <Button variant="text" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!dirty} onClick={() => onSave({ scope: draft.scope, rules: draft.rules, delivery: draft.delivery })}>
              Save
            </Button>
          </Flx>
        </Flx>
      </Flx>
    </Dialog>
  );
};

AlertsDialog.propTypes = {
  open: PropTypes.bool,
  prefs: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onTestEmail: PropTypes.func.isRequired,
};
